/**
 * Contact types, helper functions, and fake data for development.
 * Frontend-only — no backend, sync, or API dependencies.
 */

export type Phone = { label: string; number: string; primary?: boolean };
export type Email = { label: string; address: string };
export type Social = {
  platform: string;
  handle: string;
  url?: string;
  iconKey?: string;
  label?: string;
};
export type Address = {
  label: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

export type Job = {
  title: string;
  company: string;
  industry?: string;
  department?: string;
  current: boolean;
  startDate?: string;
  endDate?: string;
};

export type Relation = { label: string; name: string };
export type ContactDate = { label: string; date: string };

export type Education = {
  school: string;
  degree?: string;
  field?: string;
  year?: number;
  startYear?: number;
  endYear?: number;
};

export type ContactStatus = "connected" | "attention" | "path";
export type ContactSource = "phone" | "manual" | "linkedin" | "csv" | "apple";

export type Contact = {
  id: string;
  appleContactId?: string;
  appleContactLinked?: boolean;
  firstName: string;
  lastName: string;
  middleName?: string;
  namePrefix?: string;
  nameSuffix?: string;
  photo?: string;
  birthday?: string;
  bio?: string;
  phones: Phone[];
  emails: Email[];
  socials: Social[];
  addresses: Address[];
  currentCity?: string;
  currentState?: string;
  jobs: Job[];
  education: Education[];
  skills: string[];
  relations?: Relation[];
  dates?: ContactDate[];
  favorite?: boolean;
  lastViewedAt?: string;
  viewCount?: number;
  nickname?: string;
  tags: string[];
  status: ContactStatus;
  notes: string;
  move: string | null;
  path: string[] | null;
  claimed: boolean;
  claimedAt?: string;
  connectionStatus?: "none" | "pending" | "connected" | "blocked";
  source: ContactSource;
  lastInteraction?: string;
  interactionCount: number;
  createdAt: string;
  updatedAt: string;
  enrichment?: {
    confidence: number;
    linkedIn?: string;
    inferredSalary?: string;
    connectionCount?: number;
    lastEnrichedAt: string;
  };
};

/* Helpers */

export function getInitials(c: Contact): string {
  const f = c.firstName?.[0] ?? "";
  const l = c.lastName?.[0] ?? "";
  return f || l ? `${f}${l}` : "?";
}

export function getFullName(c: Contact): string {
  return `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "Unknown";
}

export function getAvatarColors(c: Contact): [string, string] {
  const name = `${c.firstName}${c.lastName}${c.id}`;
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue1 = Math.abs(hash) % 360;
  const hue2 = (hue1 + 40) % 360;
  return [`hsl(${hue1}, 50%, 40%)`, `hsl(${hue2}, 45%, 30%)`];
}

export function getCurrentJob(c: Contact): Job | undefined {
  return c.jobs.find((j) => j.current);
}

export function getRoleDisplay(c: Contact): string {
  const job = getCurrentJob(c);
  if (!job) return "";
  return job.company ? `${job.title} · ${job.company}` : job.title;
}

export function getPrimaryPhone(c: Contact): string {
  return c.phones[0]?.number ?? "";
}

export function getPrimaryEmail(c: Contact): string {
  return c.emails[0]?.address ?? "";
}

export function searchContacts(contacts: Contact[], query: string): Contact[] {
  const q = query.toLowerCase();
  return contacts.filter(
    (c) =>
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      (c.nickname?.toLowerCase().includes(q) ?? false) ||
      c.jobs.some(
        (j) =>
          j.title.toLowerCase().includes(q) ||
          j.company.toLowerCase().includes(q) ||
          (j.industry?.toLowerCase().includes(q) ?? false)
      ) ||
      c.skills.some((s) => s.toLowerCase().includes(q)) ||
      c.tags.some((t) => t.includes(q))
  );
}

/**
 * Dev-only fake contacts. Not used on iOS — contacts come from Apple.
 * Kept as a reference for the Contact shape during desktop development.
 */
export const DEV_CONTACTS: Contact[] = [
  {
    id: "1",
    firstName: "Lisa",
    lastName: "Nguyen",
    photo: "https://i.pravatar.cc/200?img=47",
    birthday: "1992-06-14",
    phones: [
      { label: "mobile", number: "+14155550101" },
      { label: "work", number: "+14155550201" },
    ],
    emails: [
      { label: "work", address: "lisa.nguyen@paloaltonetworks.com" },
      { label: "personal", address: "lisanguyen92@gmail.com" },
    ],
    socials: [
      { platform: "linkedin", handle: "lisanguyen-panw" },
      { platform: "twitter", handle: "@lisasells" },
      { platform: "instagram", handle: "@lisanguyen" },
      { platform: "facebook", handle: "lisa.nguyen.92" },
    ],
    addresses: [{ label: "home", city: "San Francisco", state: "CA", zip: "94107" }],
    currentCity: "San Francisco",
    currentState: "CA",
    jobs: [
      { title: "Senior Account Executive", company: "Palo Alto Networks", industry: "Cybersecurity", current: true, startDate: "2022-03" },
      { title: "Account Executive", company: "CrowdStrike", industry: "Cybersecurity", current: false, startDate: "2019-06", endDate: "2022-02" },
    ],
    education: [{ school: "UC Berkeley", degree: "BA", field: "Business Administration", year: 2014 }],
    skills: ["enterprise sales", "healthcare vertical", "cybersecurity", "channel partnerships"],
    tags: ["sales", "cybersecurity", "connector"],
    status: "connected",
    notes: "Just promoted. Expanding into healthcare vertical. Needs enterprise leads.",
    move: "Send her the 3 healthcare leads. She'll remember this.",
    path: null,
    claimed: false,
    connectionStatus: "none",
    source: "phone",
    lastInteraction: "2 days ago",
    interactionCount: 34,
    createdAt: "2024-01-15T00:00:00Z",
    updatedAt: "2026-03-18T00:00:00Z",
    enrichment: { confidence: 0.92, linkedIn: "lisanguyen-panw", inferredSalary: "$150,000-$200,000", connectionCount: 4200, lastEnrichedAt: "2026-03-01T00:00:00Z" },
  },
  {
    id: "2",
    firstName: "Mike",
    lastName: "Torres",
    photo: "https://i.pravatar.cc/200?img=12",
    birthday: "1985-11-03",
    phones: [{ label: "mobile", number: "+12125550102", primary: true }],
    emails: [
      { label: "work", address: "mtorres@deloitte.com" },
      { label: "personal", address: "mike.torres85@gmail.com" },
    ],
    socials: [{ platform: "linkedin", handle: "miketorres-deloitte" }],
    addresses: [{ label: "home", city: "Hoboken", state: "NJ", zip: "07030" }],
    currentCity: "Hoboken",
    currentState: "NJ",
    jobs: [
      { title: "VP of IT", company: "Deloitte", industry: "Consulting", current: true, startDate: "2020-01" },
      { title: "Director of IT", company: "Deloitte", industry: "Consulting", current: false, startDate: "2017-03", endDate: "2019-12" },
    ],
    education: [{ school: "Stevens Institute of Technology", degree: "MS", field: "Computer Science", year: 2010 }],
    skills: ["IT strategy", "vendor management", "cybersecurity", "cloud infrastructure"],
    tags: ["inner-circle", "connector"],
    status: "connected",
    notes: "Most reliable bridge in the network. Quietly stressed — company restructuring.",
    move: "Check if he made the Sarah intro.",
    path: null,
    claimed: true,
    claimedAt: "2026-03-18T00:00:00Z",
    connectionStatus: "pending",
    bio: "VP of IT at Deloitte. Connector.",
    source: "phone",
    lastInteraction: "5 days ago",
    interactionCount: 67,
    createdAt: "2023-06-10T00:00:00Z",
    updatedAt: "2026-03-15T00:00:00Z",
    enrichment: { confidence: 0.95, linkedIn: "miketorres-deloitte", inferredSalary: "$200,000-$280,000", connectionCount: 9200, lastEnrichedAt: "2026-03-01T00:00:00Z" },
  },
  {
    id: "3",
    firstName: "David",
    lastName: "Park",
    photo: "https://i.pravatar.cc/200?img=68",
    birthday: "1990-02-28",
    phones: [{ label: "mobile", number: "+12015550103" }, { label: "work", number: "+12015550303" }],
    emails: [{ label: "work", address: "david.park@crowdstrike.com" }],
    socials: [{ platform: "linkedin", handle: "davidpark-cs" }, { platform: "instagram", handle: "@dparklife" }, { platform: "tiktok", handle: "@dparklife" }, { platform: "reddit", handle: "u/dparkcs" }],
    addresses: [{ label: "home", city: "Jersey City", state: "NJ", zip: "07302" }],
    currentCity: "Jersey City",
    currentState: "NJ",
    jobs: [{ title: "Sales Director", company: "CrowdStrike", industry: "Cybersecurity", current: true, startDate: "2021-06" }],
    education: [{ school: "Rutgers University", degree: "BS", field: "Information Technology", year: 2012 }],
    skills: ["enterprise sales", "pharma vertical", "cybersecurity", "team leadership"],
    tags: ["sales", "cybersecurity", "alumni"],
    status: "attention",
    notes: "Pushing into pharma vertical. Bridge to Rachel Kim — $12M security budget at Pfizer.",
    move: "Send the Merck contact today. Two days late.",
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "1 week ago",
    interactionCount: 45,
    createdAt: "2023-04-20T00:00:00Z",
    updatedAt: "2026-03-13T00:00:00Z",
  },
  {
    id: "4",
    firstName: "Sarah",
    lastName: "Chen",
    photo: "https://i.pravatar.cc/200?img=49",
    phones: [{ label: "work", number: "+12125550104" }],
    emails: [{ label: "work", address: "sarah.chen@jpmorgan.com" }],
    socials: [{ platform: "linkedin", handle: "sarahchen-ciso" }],
    addresses: [],
    currentCity: "New York",
    currentState: "NY",
    jobs: [{ title: "CISO", company: "JPMorgan Chase", industry: "Financial Services", current: true, startDate: "2019-08" }],
    education: [{ school: "MIT", degree: "MS", field: "Cybersecurity", year: 2008 }],
    skills: ["CISO", "risk management", "security architecture", "compliance"],
    tags: ["cybersecurity", "prospect"],
    status: "path",
    notes: "$50M+ security budget. Evaluating endpoint solutions. Through Mike.",
    move: "Wait for Mike's intro.",
    path: ["You", "Mike Torres", "Sarah Chen"],
    source: "manual",
    claimed: false,
    interactionCount: 0,
    createdAt: "2026-03-10T00:00:00Z",
    updatedAt: "2026-03-10T00:00:00Z",
    enrichment: { confidence: 0.88, linkedIn: "sarahchen-ciso", inferredSalary: "$350,000-$500,000", connectionCount: 12400, lastEnrichedAt: "2026-03-10T00:00:00Z" },
  },
  {
    id: "5",
    firstName: "Mary",
    lastName: "Johnson",
    nickname: "Mom",
    photo: "https://i.pravatar.cc/200?img=32",
    birthday: "1962-09-22",
    phones: [{ label: "mobile", number: "+12015550001" }],
    emails: [{ label: "personal", address: "maryjohnson62@aol.com" }],
    socials: [],
    addresses: [{ label: "home", street: "42 Oak Lane", city: "West Orange", state: "NJ", zip: "07052" }],
    currentCity: "West Orange",
    currentState: "NJ",
    jobs: [{ title: "Retired Nurse", company: "St. Barnabas Medical Center", industry: "Healthcare", current: false, endDate: "2020-06" }],
    education: [{ school: "Rutgers University", degree: "BSN", field: "Nursing", year: 1984 }],
    skills: [],
    tags: ["family", "inner-circle"],
    status: "attention",
    notes: "Dad's appointment Tuesday. She deflected when asked how she was.",
    move: "Call Sunday. Ask about her, not just dad. Listen.",
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "3 days ago",
    interactionCount: 312,
    createdAt: "2023-01-01T00:00:00Z",
    updatedAt: "2026-03-17T00:00:00Z",
  },
  {
    id: "6",
    firstName: "Rachel",
    lastName: "Kim",
    photo: "https://i.pravatar.cc/200?img=23",
    phones: [{ label: "work", number: "+12125550106" }],
    emails: [{ label: "work", address: "rachel.kim@pfizer.com" }],
    socials: [{ platform: "linkedin", handle: "rachelkim-security" }],
    addresses: [],
    currentCity: "New York",
    currentState: "NY",
    jobs: [{ title: "Director of Security", company: "Pfizer", industry: "Pharmaceutical", current: true, startDate: "2021-01" }],
    education: [{ school: "Carnegie Mellon", degree: "MS", field: "Information Security", year: 2012 }],
    skills: ["security operations", "pharmaceutical compliance", "vendor evaluation"],
    tags: ["healthcare", "cybersecurity", "prospect"],
    status: "path",
    notes: "$12M security modernization. Through David Park.",
    move: "Deliver for David first, then ask for the intro.",
    path: ["You", "David Park", "Rachel Kim"],
    source: "manual",
    claimed: false,
    interactionCount: 0,
    createdAt: "2026-03-12T00:00:00Z",
    updatedAt: "2026-03-12T00:00:00Z",
  },
  {
    id: "7",
    firstName: "James",
    lastName: "Wilson",
    photo: "https://i.pravatar.cc/200?img=53",
    phones: [{ label: "work", number: "+12125550107" }],
    emails: [{ label: "work", address: "jwilson@mountsinai.org" }],
    socials: [{ platform: "linkedin", handle: "jameswilson-cto" }],
    addresses: [],
    currentCity: "New York",
    currentState: "NY",
    jobs: [{ title: "CTO", company: "Mount Sinai Health", industry: "Healthcare", current: true, startDate: "2018-05" }],
    education: [{ school: "Columbia University", degree: "MBA", year: 2010 }],
    skills: ["health IT", "digital transformation", "infrastructure"],
    tags: ["healthcare", "prospect"],
    status: "path",
    notes: "$8M infrastructure stack. Lisa co-presented with him at summit.",
    move: "Help Lisa first. She opens this door.",
    path: ["You", "Lisa Nguyen", "James Wilson"],
    source: "manual",
    claimed: false,
    interactionCount: 0,
    createdAt: "2026-03-14T00:00:00Z",
    updatedAt: "2026-03-14T00:00:00Z",
  },
  {
    id: "8",
    firstName: "Tom",
    lastName: "Bradley",
    photo: "https://i.pravatar.cc/200?img=7",
    birthday: "1982-07-19",
    phones: [{ label: "mobile", number: "+19085550108" }],
    emails: [{ label: "work", address: "tom@securenetsolutions.com" }],
    socials: [{ platform: "linkedin", handle: "tombradley-ceo" }],
    addresses: [{ label: "office", city: "Edison", state: "NJ", zip: "08817" }],
    currentCity: "Edison",
    currentState: "NJ",
    jobs: [{ title: "CEO", company: "SecureNet Solutions", industry: "Cybersecurity", current: true, startDate: "2015-01" }],
    education: [{ school: "NJIT", degree: "BS", field: "Computer Science", year: 2004 }],
    skills: ["leadership", "cybersecurity", "startups", "networking"],
    tags: ["cybersecurity", "friend", "alumni"],
    status: "connected",
    notes: "Tight bond. Runs the cybersecurity roundtable. Good for warm intros.",
    move: null,
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "2 weeks ago",
    interactionCount: 28,
    createdAt: "2023-02-14T00:00:00Z",
    updatedAt: "2026-03-06T00:00:00Z",
  },
  {
    id: "9",
    firstName: "Amy",
    lastName: "Liu",
    photo: "https://i.pravatar.cc/200?img=25",
    birthday: "1988-12-05",
    phones: [{ label: "mobile", number: "+12015550109" }],
    emails: [{ label: "work", address: "amy@liuassociates.com" }],
    socials: [{ platform: "linkedin", handle: "amyliu-cpa" }],
    addresses: [{ label: "office", city: "Fort Lee", state: "NJ", zip: "07024" }],
    currentCity: "Fort Lee",
    currentState: "NJ",
    jobs: [{ title: "CPA", company: "Liu & Associates", industry: "Financial Services", current: true, startDate: "2016-04" }],
    education: [{ school: "Baruch College", degree: "BS", field: "Accounting", year: 2010 }],
    skills: ["tax advisory", "small business accounting", "estate planning"],
    tags: ["friend"],
    status: "connected",
    notes: "Tax advisor. Solid. Referred 2 clients last year.",
    move: null,
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "1 month ago",
    interactionCount: 12,
    createdAt: "2024-03-20T00:00:00Z",
    updatedAt: "2026-02-18T00:00:00Z",
  },
  {
    id: "10",
    firstName: "Carlos",
    lastName: "Reyes",
    photo: "https://i.pravatar.cc/200?img=14",
    birthday: "1987-04-11",
    phones: [{ label: "mobile", number: "+17325550110" }, { label: "work", number: "+17325550210" }],
    emails: [{ label: "work", address: "carlos.reyes@fortinet.com" }],
    socials: [{ platform: "linkedin", handle: "carlosreyes-fortinet" }, { platform: "twitter", handle: "@creyescyber" }],
    addresses: [{ label: "home", city: "Princeton", state: "NJ", zip: "08540" }],
    currentCity: "Princeton",
    currentState: "NJ",
    jobs: [
      { title: "VP of Sales", company: "Fortinet", industry: "Cybersecurity", current: true, startDate: "2021-09" },
      { title: "Regional Director", company: "Cisco Security", industry: "Cybersecurity", current: false, startDate: "2018-01", endDate: "2021-08" },
    ],
    education: [{ school: "Penn State", degree: "BS", field: "Marketing", year: 2009 }],
    skills: ["enterprise sales", "channel strategy", "cybersecurity", "pipeline management"],
    tags: ["sales", "cybersecurity", "connector"],
    status: "connected",
    notes: "Non-competing. Shares deal intel. Joint pipeline reviews monthly.",
    move: null,
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "4 days ago",
    interactionCount: 41,
    createdAt: "2023-09-05T00:00:00Z",
    updatedAt: "2026-03-16T00:00:00Z",
  },
  {
    id: "11",
    firstName: "Priya",
    lastName: "Sharma",
    photo: "https://i.pravatar.cc/200?img=44",
    birthday: "1993-08-30",
    phones: [{ label: "mobile", number: "+14085550111", primary: true }],
    emails: [{ label: "personal", address: "priya.sharma93@gmail.com" }],
    socials: [
      { platform: "linkedin", handle: "priyasharma-google" },
      { platform: "instagram", handle: "@priyabuilds" },
      { platform: "github", handle: "priyasharma" },
      { platform: "threads", handle: "@priyabuilds" },
      { platform: "twitter", handle: "@priyabuilds" },
    ],
    addresses: [{ label: "home", city: "Mountain View", state: "CA", zip: "94041" }],
    currentCity: "Mountain View",
    currentState: "CA",
    jobs: [
      { title: "Engineering Manager", company: "Google", industry: "Technology", current: true, startDate: "2022-01" },
      { title: "Senior Software Engineer", company: "Google", industry: "Technology", current: false, startDate: "2018-06", endDate: "2021-12" },
    ],
    education: [{ school: "Rutgers University", degree: "BS", field: "Computer Science", year: 2015 }],
    skills: ["engineering leadership", "distributed systems", "mentorship"],
    tags: ["friend", "mentor"],
    status: "connected",
    notes: "College friend. Rose fast. Great for tech industry intros.",
    move: null,
    path: null,
    claimed: true,
    claimedAt: "2026-03-15T00:00:00Z",
    connectionStatus: "connected",
    bio: "Engineering at Google. Passionate about mentorship and distributed systems.",
    source: "phone",
    lastInteraction: "2 weeks ago",
    interactionCount: 23,
    createdAt: "2023-01-01T00:00:00Z",
    updatedAt: "2026-03-06T00:00:00Z",
  },
  {
    id: "12",
    firstName: "Jake",
    lastName: "Thompson",
    photo: "https://i.pravatar.cc/200?img=11",
    birthday: "1991-01-17",
    phones: [{ label: "mobile", number: "+19735550112" }],
    emails: [{ label: "personal", address: "jake.t@greenleafdispensary.com" }],
    socials: [{ platform: "instagram", handle: "@greenleafnj" }],
    addresses: [{ label: "home", city: "Montclair", state: "NJ", zip: "07042" }],
    currentCity: "Montclair",
    currentState: "NJ",
    jobs: [{ title: "Owner", company: "Green Leaf Dispensary", industry: "Cannabis", current: true, startDate: "2020-01" }],
    education: [],
    skills: ["small business", "retail operations", "cannabis industry"],
    tags: ["friend"],
    status: "connected",
    notes: "High school friend. Runs 3 locations in Jersey.",
    move: null,
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "3 weeks ago",
    interactionCount: 8,
    createdAt: "2024-06-10T00:00:00Z",
    updatedAt: "2026-02-27T00:00:00Z",
  },
  {
    id: "13",
    firstName: "Maria",
    lastName: "Santos",
    photo: "https://i.pravatar.cc/200?img=27",
    birthday: "1978-03-15",
    phones: [{ label: "mobile", number: "+12015550113" }, { label: "office", number: "+12015550313" }],
    emails: [{ label: "work", address: "msantos@santosvega.com" }],
    socials: [{ platform: "linkedin", handle: "mariasantos-attorney" }],
    addresses: [{ label: "office", street: "200 Main St", city: "Hackensack", state: "NJ", zip: "07601" }],
    currentCity: "Hackensack",
    currentState: "NJ",
    jobs: [{ title: "Managing Partner", company: "Santos & Vega LLP", industry: "Legal", current: true, startDate: "2010-06" }],
    education: [{ school: "Seton Hall Law", degree: "JD", year: 2003 }, { school: "Rutgers University", degree: "BA", field: "Political Science", year: 2000 }],
    skills: ["corporate law", "estate planning", "contracts", "litigation"],
    tags: ["family", "friend"],
    status: "connected",
    notes: "Family lawyer. Handles everything from contracts to estate.",
    move: null,
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "1 week ago",
    interactionCount: 19,
    createdAt: "2023-03-01T00:00:00Z",
    updatedAt: "2026-03-13T00:00:00Z",
  },
  {
    id: "14",
    firstName: "Anthony",
    lastName: "Russo",
    nickname: "Tony",
    photo: "https://i.pravatar.cc/200?img=60",
    birthday: "1975-10-08",
    phones: [{ label: "mobile", number: "+19085550114" }],
    emails: [],
    socials: [],
    addresses: [{ label: "home", city: "Clark", state: "NJ", zip: "07066" }],
    currentCity: "Clark",
    currentState: "NJ",
    jobs: [{ title: "Owner", company: "Russo Plumbing", industry: "Home Services", current: true, startDate: "2005-03" }],
    education: [],
    skills: ["plumbing", "home repair", "contractor"],
    tags: ["friend"],
    status: "connected",
    notes: "Best plumber in the area. Reliable. Reasonable prices.",
    move: null,
    path: null,
    source: "phone",
    claimed: false,
    lastInteraction: "2 months ago",
    interactionCount: 6,
    createdAt: "2024-08-15T00:00:00Z",
    updatedAt: "2026-01-20T00:00:00Z",
  },
  {
    id: "15",
    firstName: "Chaz",
    lastName: "Mitchell",
    photo: "https://i.pravatar.cc/200?img=8",
    birthday: "1991-05-22",
    phones: [{ label: "mobile", number: "+12015550115", primary: true }, { label: "work", number: "+12015550315" }],
    emails: [{ label: "work", address: "chaz.mitchell@crowdstrike.com" }, { label: "personal", address: "chazmitch@gmail.com" }],
    socials: [
      { platform: "linkedin", handle: "chazmitchell" },
      { platform: "instagram", handle: "@chazmitch" },
      { platform: "twitter", handle: "@chazmitchell" },
      { platform: "discord", handle: "chazmitch" },
      { platform: "spotify", handle: "chazmitchell" },
      { platform: "venmo", handle: "Chaz-Mitchell" },
    ],
    addresses: [{ label: "home", city: "Montclair", state: "NJ", zip: "07042" }],
    currentCity: "Montclair",
    currentState: "NJ",
    jobs: [
      { title: "Sales Manager", company: "CrowdStrike", industry: "Cybersecurity", current: true, startDate: "2020-09" },
      { title: "Account Executive", company: "CrowdStrike", industry: "Cybersecurity", current: false, startDate: "2018-01", endDate: "2020-08" },
    ],
    education: [{ school: "TCNJ", degree: "BS", field: "Business", year: 2013 }],
    skills: ["enterprise sales", "cybersecurity", "networking", "leadership"],
    tags: ["inner-circle", "sales", "cybersecurity", "alumni"],
    status: "connected",
    notes: "Best friend. Co-founder vibes. Imports his contacts too — massive overlap in network.",
    move: null,
    path: null,
    claimed: true,
    claimedAt: "2026-03-01T00:00:00Z",
    connectionStatus: "connected",
    bio: "Sales leader. Building in public. Meridian early adopter.",
    source: "phone",
    lastInteraction: "yesterday",
    interactionCount: 482,
    createdAt: "2023-01-01T00:00:00Z",
    updatedAt: "2026-03-19T00:00:00Z",
  },
];
